// src/routes/MainRoutes.js
import { lazy } from 'react';
import { Navigate } from 'react-router';

// project import
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import ProtectedRoute from 'routes/ProtectedRoute';

// render - dashboard
const DashboardDefault = Loadable(lazy(() => import('pages/dashboard/default')));

const UsersPage = Loadable(lazy(() => import('pages/admin/users')));

//projects
const ProjectList = Loadable(lazy(() => import('pages/admin/projects/ProjectList')));
const ProjectCreate = Loadable(lazy(() => import('pages/admin/projects/ProjectCreate')));
const ProjectEdit = Loadable(lazy(() => import('pages/admin/projects/ProjectEdit')));
const ProjectDetails = Loadable(lazy(() => import('pages/admin/projects/ProjectDetails')));

//Vendor Invoice Routes
const VendorInvoiceList = Loadable(lazy(() => import('pages/vendor/invoices/InvoiceList')));
const VendorInvoiceCreate = Loadable(lazy(() => import('pages/vendor/invoices/InvoiceCreate')));
const VendorInvoiceEdit = Loadable(lazy(() => import('pages/vendor/invoices/InvoiceEdit')));
const VendorInvoiceDetails = Loadable(lazy(() => import('pages/vendor/invoices/InvoiceDetails')));

//Vendor Subcontractor Routes
const VendorSubcontractorList = Loadable(lazy(() => import('pages/vendor/subcontractors/SubcontractorList')));
const VendorSubcontractorForm = Loadable(lazy(() => import('pages/vendor/subcontractors/SubcontractorForm')));

//Vendor Time Tracking Routes
const VendorTimeTracking = Loadable(lazy(() => import('pages/vendor/timetracking/TimeTracking')));

//Admin Time Tracking Routes
const AdminTimeTracking = Loadable(lazy(() => import('pages/admin/timetracking/TimeTrackingAdmin')));

//Subcontractor Time Tracking Routes
const SubcontractorTimeTracking = Loadable(lazy(() => import('pages/subcontractor/timetracking/TimeTracking')));

// OPEX Vendor Invoice Routes
const OpexVendorInvoiceList = Loadable(lazy(() => import('pages/opex-vendor/invoices/InvoiceList')));
const OpexVendorInvoiceCreate = Loadable(lazy(() => import('pages/opex-vendor/invoices/InvoiceCreate')));
const OpexVendorInvoiceEdit = Loadable(lazy(() => import('pages/opex-vendor/invoices/InvoiceEdit')));
const OpexVendorInvoiceDetails = Loadable(lazy(() => import('pages/opex-vendor/invoices/InvoiceDetails')));

// Client Timesheets Routes (for approving vendor work)
import ClientTimesheetsList from 'pages/client/timesheets/TimesheetsList';

// Client Billing Invoice Routes (revenue invoices from admin)
import BillingInvoiceList from 'pages/client/billing-invoices/BillingInvoiceList';
import BillingInvoiceDetails from 'pages/client/billing-invoices/BillingInvoiceDetails';

// Admin Invoice Routes
import AdminInvoiceList from 'pages/admin/invoices/InvoiceList';
import AdminInvoiceDetails from 'pages/admin/invoices/InvoiceDetails';

// Admin OPEX Invoice Routes
import OpexInvoiceList from 'pages/admin/opex/OpexInvoiceList';
import OpexInvoiceDetails from 'pages/admin/opex/OpexInvoiceDetails';

// Admin Payments Routes
const PaymentsView = Loadable(lazy(() => import('pages/admin/payments/PaymentsView')));

// Admin Settings Routes
const AdminSettings = Loadable(lazy(() => import('pages/admin/Settings')));

// Admin Client Invoices (Revenue Invoices sent to clients) Routes
const AdminClientInvoicesList = Loadable(lazy(() => import('pages/admin/client-invoices/ClientInvoicesList')));
const AdminClientInvoiceForm = Loadable(lazy(() => import('pages/admin/client-invoices/ClientInvoiceForm')));
const AdminClientInvoiceDetails = Loadable(lazy(() => import('pages/admin/client-invoices/ClientInvoiceDetails')));

// Profile Routes
const Profile = Loadable(lazy(() => import('pages/profile/Profile')));

// Vacation Routes
const MyVacation = Loadable(lazy(() => import('pages/vacation/MyVacation')));
const AdminVacationManagement = Loadable(lazy(() => import('pages/admin/VacationManagement')));

// Bank Account Management Routes
const BankAccountManagement = Loadable(lazy(() => import('pages/admin/BankAccountManagement')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: (
    <ProtectedRoute>
      <DashboardLayout />
    </ProtectedRoute>
  ),
  children: [
    {
      path: '/',
      element: <Navigate to="/dashboard" replace />
    },
    {
      path: 'dashboard',
      element: <DashboardDefault />
    },
    {
      path: 'profile',
      element: <Profile />
    },
    {
      path: 'vacation',
      element: <MyVacation />
    },
    {
      path: 'admin',
      children: [
        {
          path: 'users',
          element: (
            <ProtectedRoute allowedRoles={['admin']}>
              <UsersPage />
            </ProtectedRoute>
          )
        },
        {
          path: 'projects',
          children: [
            {
              path: '',
              element: <ProjectList />
            },
            {
              path: 'create',
              element: <ProjectCreate />
            },
            {
              path: ':id',
              element: <ProjectDetails />
            },
            {
              path: ':id/edit',
              element: <ProjectEdit />
            }
          ]
        },
        {
          path: 'invoices',
          children: [
            {
              path: '',
              element: <AdminInvoiceList />
            },
            {
              path: ':id',
              element: <AdminInvoiceDetails />
            }
          ]
        },
        {
          path: 'opex-invoices',
          children: [
            {
              path: '',
              element: (
                <ProtectedRoute allowedRoles={['admin']}>
                  <OpexInvoiceList />
                </ProtectedRoute>
              )
            },
            {
              path: ':id',
              element: (
                <ProtectedRoute allowedRoles={['admin']}>
                  <OpexInvoiceDetails />
                </ProtectedRoute>
              )
            }
          ]
        },
        {
          path: 'payments',
          element: (
            <ProtectedRoute allowedRoles={['admin']}>
              <PaymentsView />
            </ProtectedRoute>
          )
        },
        {
          path: 'settings',
          element: (
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminSettings />
            </ProtectedRoute>
          )
        },
        {
          path: 'client-invoices',
          children: [
            {
              path: '',
              element: (
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminClientInvoicesList />
                </ProtectedRoute>
              )
            },
            {
              path: 'new',
              element: (
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminClientInvoiceForm />
                </ProtectedRoute>
              )
            },
            {
              path: ':id',
              element: (
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminClientInvoiceDetails />
                </ProtectedRoute>
              )
            },
            {
              path: ':id/edit',
              element: (
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminClientInvoiceForm />
                </ProtectedRoute>
              )
            }
          ]
        },
        {
          path: 'timetracking',
          element: (
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminTimeTracking />
            </ProtectedRoute>
          )
        },
        {
          path: 'vacation',
          element: (
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminVacationManagement />
            </ProtectedRoute>
          )
        },
        {
          path: 'vendors-bank-accounts',
          element: (
            <ProtectedRoute allowedRoles={['admin']}>
              <BankAccountManagement />
            </ProtectedRoute>
          )
        }
      ]
    },
    {
      path: 'vendor',
      children: [
        {
          path: 'timetracking',
          element: (
            <ProtectedRoute allowedRoles={['vendor']}>
              <VendorTimeTracking />
            </ProtectedRoute>
          )
        },
        {
          path: 'invoices',
          children: [
            {
              path: '',
              element: <VendorInvoiceList />
            },
            {
              path: 'create',
              element: <VendorInvoiceCreate />
            },
            {
              path: ':id',
              element: <VendorInvoiceDetails />
            },
            {
              path: ':id/edit',
              element: <VendorInvoiceEdit />
            }
          ]
        },
        {
          path: 'subcontractors',
          children: [
            {
              path: '',
              element: (
                <ProtectedRoute allowedRoles={['vendor']}>
                  <VendorSubcontractorList />
                </ProtectedRoute>
              )
            },
            {
              path: 'new',
              element: (
                <ProtectedRoute allowedRoles={['vendor']}>
                  <VendorSubcontractorForm />
                </ProtectedRoute>
              )
            },
            {
              path: ':id/edit',
              element: (
                <ProtectedRoute allowedRoles={['vendor']}>
                  <VendorSubcontractorForm />
                </ProtectedRoute>
              )
            }
          ]
        }
      ]
    },
    {
      path: 'opex-vendor',
      children: [
        {
          path: 'invoices',
          children: [
            {
              path: '',
              element: (
                <ProtectedRoute allowedRoles={['opex_vendor']}>
                  <OpexVendorInvoiceList />
                </ProtectedRoute>
              )
            },
            {
              path: 'create',
              element: (
                <ProtectedRoute allowedRoles={['opex_vendor']}>
                  <OpexVendorInvoiceCreate />
                </ProtectedRoute>
              )
            },
            {
              path: ':id',
              element: (
                <ProtectedRoute allowedRoles={['opex_vendor']}>
                  <OpexVendorInvoiceDetails />
                </ProtectedRoute>
              )
            },
            {
              path: ':id/edit',
              element: (
                <ProtectedRoute allowedRoles={['opex_vendor']}>
                  <OpexVendorInvoiceEdit />
                </ProtectedRoute>
              )
            }
          ]
        }
      ]
    },
    {
      path: 'client',
      children: [
        {
          path: 'billing-invoices',
          children: [
            {
              path: '',
              element: <BillingInvoiceList />
            },
            {
              path: ':id',
              element: <BillingInvoiceDetails />
            }
          ]
        },
        {
          path: 'timesheets',
          element: <ClientTimesheetsList />
        }
      ]
    },
    {
      path: 'subcontractor',
      children: [
        {
          path: 'timetracking',
          element: (
            <ProtectedRoute allowedRoles={['subcontractor']}>
              <SubcontractorTimeTracking />
            </ProtectedRoute>
          )
        }
      ]
    }
  ]
};

export default MainRoutes;
